export default function MarketStabilitySection() {
  return (
    <section className="relative py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-slate-100 rounded-md border border-slate-200 mb-6">
            <i className="ri-restaurant-line text-slate-600 text-sm"></i>
            <span className="text-slate-600 text-sm font-medium tracking-wide">市場論證</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-6">
            人流成長如何轉化為
            <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-blue-500">餐飲的全年穩定市場</span>
          </h2>

          <p className="text-lg text-slate-600 leading-relaxed">
            新碼頭與翔安機場帶來的不僅是人流數字的增長，更重要的是<strong className="text-slate-900">人流結構的優化</strong>與<strong className="text-slate-900">全年穩定性的提升</strong>，這對餐飲業態而言是最關鍵的市場基礎。
          </p>
        </div>

        {/* Three Key Points */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Point 1 */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-8 border-2 border-orange-200 shadow-lg">
            <div className="w-14 h-14 flex items-center justify-center bg-orange-500 rounded-md mb-6">
              <i className="ri-calendar-line text-white text-2xl"></i>
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-4">平日與非旺季底盤加厚</h3>
            <p className="text-slate-600 text-sm leading-relaxed mb-4">
              新碼頭與翔安機場帶來的增量主要反映在<strong className="text-orange-700">平日與非旺季時段</strong>，有效降低餐飲業的季節波動風險。
            </p>
            <div className="bg-white/60 rounded-lg p-4 border border-orange-200">
              <div className="text-orange-700 font-bold text-xs mb-1">關鍵影響</div>
              <p className="text-slate-600 text-xs">全年營收更平穩，不再過度依賴旺季與週末</p>
            </div>
          </div>

          {/* Point 2 */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-8 border-2 border-blue-200 shadow-lg">
            <div className="w-14 h-14 flex items-center justify-center bg-blue-500 rounded-md mb-6">
              <i className="ri-time-line text-white text-2xl"></i>
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-4">短停留旅客外食頻次高</h3>
            <p className="text-slate-600 text-sm leading-relaxed mb-4">
              短停留旅客（1-3天）的<strong className="text-blue-700">外食頻率遠高於長停留旅客</strong>，餐飲成為第一接觸型消費，轉換率更高。
            </p>
            <div className="bg-white/60 rounded-lg p-4 border border-blue-200">
              <div className="text-blue-700 font-bold text-xs mb-1">關鍵影響</div>
              <p className="text-slate-600 text-xs">每位旅客的餐飲消費頻次與客單價提升</p>
            </div>
          </div>

          {/* Point 3 */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-8 border-2 border-green-200 shadow-lg">
            <div className="w-14 h-14 flex items-center justify-center bg-green-500 rounded-md mb-6">
              <i className="ri-store-line text-white text-2xl"></i>
            </div>
            <h3 className="text-xl font-black text-slate-900 mb-4">連鎖標準化業態更吃香</h3>
            <p className="text-slate-600 text-sm leading-relaxed mb-4">
              短停留旅客更傾向選擇<strong className="text-green-700">品牌辨識度高、決策時間短</strong>的連鎖餐飲，標準化業態具備天然優勢。
            </p>
            <div className="bg-white/60 rounded-lg p-4 border border-green-200">
              <div className="text-green-700 font-bold text-xs mb-1">關鍵影響</div>
              <p className="text-slate-600 text-xs">連鎖品牌可複製、可擴張的商業模式更適合</p>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg p-10 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 rounded-md border border-white/20 mb-6">
              <i className="ri-checkbox-circle-line text-orange-400 text-sm"></i>
              <span className="text-slate-300 text-sm font-medium">市場結論</span>
            </div>
            
            <h3 className="text-2xl font-black text-white mb-6">
              人流成長 × 結構優化 = 餐飲市場的黃金窗口
            </h3>

            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              新碼頭與翔安機場帶來的不僅是<span className="text-orange-400 font-bold">人流數字的增長</span>，更是<span className="text-blue-400 font-bold">人流結構的優化</span>。平日底盤加厚、短停留高頻消費、品牌辨識度優勢，三者疊加形成餐飲市場的<strong className="text-white">結構性利好</strong>。
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-orange-500/20 rounded-md border border-orange-400/30">
                <i className="ri-arrow-up-line text-orange-400"></i>
                <span className="text-orange-300 text-sm font-medium">全年穩定性提升</span>
              </div>
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-500/20 rounded-md border border-blue-400/30">
                <i className="ri-arrow-up-line text-blue-400"></i>
                <span className="text-blue-300 text-sm font-medium">外食頻次提升</span>
              </div>
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-green-500/20 rounded-md border border-green-400/30">
                <i className="ri-arrow-up-line text-green-400"></i>
                <span className="text-green-300 text-sm font-medium">連鎖品牌優勢</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
